import React, { useState } from 'react';
import authService from '../../../services/authService';
import './HeaderScreens.css';

const initialState = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
};

const ChangePasswordScreen = ({ onClose }) => {
  const [formValues, setFormValues] = useState(initialState);
  const [visibleFields, setVisibleFields] = useState({
    current: false,
    newValue: false,
    confirm: false
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const getLiveValidationErrors = (values) => {
    const liveErrors = {};
    const currentTrimmed = (values.currentPassword || '').trim();
    const newTrimmed = (values.newPassword || '').trim();

    if (values.newPassword) {
      const passwordError = validatePassword(values.newPassword);
      if (passwordError) {
        liveErrors.newPassword = passwordError;
      }
    }

    if (currentTrimmed && newTrimmed && currentTrimmed === newTrimmed) {
      liveErrors.newPassword = 'New password must be different from current password.';
    }

    if (values.confirmPassword && values.newPassword !== values.confirmPassword) {
      liveErrors.confirmPassword = 'Passwords do not match.';
    }

    return liveErrors;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const nextValues = { ...formValues, [name]: value };
    setFormValues(nextValues);

    const liveErrors = getLiveValidationErrors(nextValues);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.currentPassword;
      delete next.newPassword;
      delete next.confirmPassword;
      delete next.submit;
      if (!nextValues.currentPassword) {
        next.currentPassword = 'Current password is required.';
      }
      return { ...next, ...liveErrors };
    });

    if (successMessage) {
      setSuccessMessage('');
    }
  };

  const toggleVisibility = (field) => {
    setVisibleFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long.';
    }
    // Check for uppercase, lowercase, number, and special character
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
      return 'Password must contain uppercase, lowercase, numbers, and special characters.';
    }
    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrors({});
    setSuccessMessage('');

    // Validation
    const newErrors = {};

    if (!formValues.currentPassword) {
      newErrors.currentPassword = 'Current password is required.';
    }

    if (!formValues.newPassword) {
      newErrors.newPassword = 'New password is required.';
    } else {
      const passwordError = validatePassword(formValues.newPassword);
      if (passwordError) {
        newErrors.newPassword = passwordError;
      }
    }

    if (!formValues.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password.';
    } else if (formValues.newPassword !== formValues.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match.';
    }

    if (formValues.currentPassword === formValues.newPassword) {
      newErrors.newPassword = 'New password must be different from current password.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await authService.changePassword(
        formValues.currentPassword,
        formValues.newPassword
      );

      if (response.data && response.data.success) {
        setSuccessMessage(response.data.message || 'Password changed successfully!');
        setFormValues(initialState);

        // Close after 2 seconds
        setTimeout(() => {
          onClose?.();
        }, 2000);
      } else {
        setErrors({ submit: response.data?.message || 'Failed to change password. Please try again.' });
      }
    } catch (error) {
      console.error('Change password error:', error);
      const errorMessage = error.response?.data?.message ||
        error.message ||
        'An error occurred while changing password. Please try again.';
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormValues(initialState);
    setErrors({});
    setSuccessMessage('');
    onClose?.();
  };

  const hasSameAsCurrentError =
    (formValues.currentPassword || '').trim() &&
    (formValues.newPassword || '').trim() &&
    (formValues.currentPassword || '').trim() === (formValues.newPassword || '').trim();

  const isSubmitDisabled =
    isSubmitting ||
    hasSameAsCurrentError ||
    Boolean(errors.newPassword) ||
    Boolean(errors.confirmPassword) ||
    !formValues.currentPassword ||
    !formValues.newPassword ||
    !formValues.confirmPassword;

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="modal-container change-password-card"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-header" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px', padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
            <h3 className="modal-title" style={{ color: 'var(--color-primary)' }}>Change Password</h3>
            <button
              type="button"
              className="modal-close"
              aria-label="Close change password panel"
              onClick={onClose}
            >
              &times;
            </button>
          </div>
          <p className="header-screen-subtitle">
            Keep your account secure by updating your password regularly.
          </p>
        </div>

        <form className="change-password-form" onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '20px' }}>
            {errors.submit && (
              <div className="alert alert-danger" style={{ marginBottom: '16px', padding: '10px 14px', borderRadius: 'var(--radius-sm)', fontSize: '13px' }}>
                {errors.submit}
              </div>
            )}

            {successMessage && (
              <div className="alert alert-success" style={{ marginBottom: '16px', padding: '10px 14px', borderRadius: 'var(--radius-sm)', fontSize: '13px' }}>
                {successMessage}
              </div>
            )}

            <div className="form-field">
              <label htmlFor="currentPassword">Current Password</label>
              <div className="input-with-icon">
                <input
                  id="currentPassword"
                  name="currentPassword"
                  type={visibleFields.current ? 'text' : 'password'}
                  value={formValues.currentPassword}
                  onChange={handleChange}
                  placeholder="Enter your current password"
                  required
                  className={errors.currentPassword ? 'error' : ''}
                />
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => toggleVisibility('current')}
                  aria-label="Toggle current password visibility"
                >
                  <i className={`fa ${visibleFields.current ? 'fa-eye-slash' : 'fa-eye'}`} />
                </button>
              </div>
              {errors.currentPassword && (
                <span className="error-message" style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  {errors.currentPassword}
                </span>
              )}
            </div>

            <div className="form-field">
              <label htmlFor="newPassword">
                New Password <span className="required">*</span>
                <span className="info-icon" title="Use at least 8 characters with numbers and symbols">
                  <i className="fa fa-info-circle" aria-hidden="true" />
                </span>
              </label>
              <div className="input-with-icon">
                <input
                  id="newPassword"
                  name="newPassword"
                  type={visibleFields.newValue ? 'text' : 'password'}
                  value={formValues.newPassword}
                  onChange={handleChange}
                  placeholder="Create a strong password"
                  minLength={8}
                  required
                  className={errors.newPassword ? 'error' : ''}
                />
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => toggleVisibility('newValue')}
                  aria-label="Toggle new password visibility"
                >
                  <i className={`fa ${visibleFields.newValue ? 'fa-eye-slash' : 'fa-eye'}`} />
                </button>
              </div>
              {errors.newPassword && (
                <span className="error-message" style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  {errors.newPassword}
                </span>
              )}
            </div>

            <div className="form-field">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <div className="input-with-icon">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={visibleFields.confirm ? 'text' : 'password'}
                  value={formValues.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter the new password"
                  required
                  className={errors.confirmPassword ? 'error' : ''}
                />
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => toggleVisibility('confirm')}
                  aria-label="Toggle confirm password visibility"
                >
                  <i className={`fa ${visibleFields.confirm ? 'fa-eye-slash' : 'fa-eye'}`} />
                </button>
              </div>
              {errors.confirmPassword && (
                <span className="error-message" style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  {errors.confirmPassword}
                </span>
              )}
            </div>
          </div>

          <div className="modal-footer" style={{ padding: '12px 20px' }}>
            <button type="button" className="btn-cancel" onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isSubmitDisabled}>
              {isSubmitting ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordScreen;

